/**
 * Graph Animation Utilities
 *
 * Provides animation recipes for Cytoscape.js graph transitions.
 * All animations respect the `prefers-reduced-motion` accessibility setting.
 *
 * Animation types:
 * - Node lifecycle (appear/disappear)
 * - Edge growth
 * - Layout transitions
 * - Filter transitions (show/hide node sets)
 * - Cluster expand/collapse
 * - Scope change transitions
 */

import { Core, NodeSingular, EdgeSingular } from 'cytoscape';

// Default animation settings
const DEFAULT_DURATION = 600;
const DEFAULT_EASING = 'ease-in-out-cubic';
const LARGE_GRAPH_THRESHOLD = 1000;

/**
 * Check if user prefers reduced motion (WCAG 2.1 AA compliance)
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Get effective duration based on accessibility settings and graph size
 */
function getEffectiveDuration(duration: number, nodeCount?: number): number {
  if (prefersReducedMotion()) return 0;
  // For large graphs, use faster animations to maintain performance
  if (nodeCount && nodeCount > LARGE_GRAPH_THRESHOLD) {
    return Math.min(duration, 200);
  }
  return duration;
}

/**
 * Animate a node appearing in the graph (fade-in + scale up)
 */
export function animateNodeAppear(
  cy: Core,
  nodeId: string,
  duration: number = DEFAULT_DURATION
): Promise<void> {
  const effectiveDuration = getEffectiveDuration(duration, cy.nodes().length);
  const node = cy.getElementById(nodeId) as NodeSingular;
  if (!node || node.length === 0) return Promise.resolve();

  if (effectiveDuration === 0) {
    node.style({ opacity: 1 });
    return Promise.resolve();
  }

  // Start invisible and small
  node.style({ opacity: 0 });

  return new Promise<void>((resolve) => {
    node.animate(
      { style: { opacity: 1 } },
      {
        duration: effectiveDuration,
        easing: DEFAULT_EASING,
        complete: () => resolve(),
      }
    );
  });
}

/**
 * Animate a node disappearing from the graph (fade-out + scale down)
 */
export function animateNodeDisappear(
  cy: Core,
  nodeId: string,
  duration: number = DEFAULT_DURATION
): Promise<void> {
  const effectiveDuration = getEffectiveDuration(duration, cy.nodes().length);
  const node = cy.getElementById(nodeId) as NodeSingular;
  if (!node || node.length === 0) return Promise.resolve();

  if (effectiveDuration === 0) {
    node.style({ opacity: 0 });
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    node.animate(
      { style: { opacity: 0 } },
      {
        duration: effectiveDuration,
        easing: DEFAULT_EASING,
        complete: () => resolve(),
      }
    );
  });
}

/**
 * Animate an edge growing into view (opacity fade-in)
 */
export function animateEdgeGrow(
  cy: Core,
  edgeId: string,
  duration: number = DEFAULT_DURATION
): Promise<void> {
  const effectiveDuration = getEffectiveDuration(duration, cy.nodes().length);
  const edge = cy.getElementById(edgeId) as EdgeSingular;
  if (!edge || edge.length === 0) return Promise.resolve();

  if (effectiveDuration === 0) {
    edge.style({ opacity: 1 });
    return Promise.resolve();
  }

  edge.style({ opacity: 0 });

  return new Promise<void>((resolve) => {
    edge.animate(
      { style: { opacity: 1 } },
      {
        duration: effectiveDuration,
        easing: DEFAULT_EASING,
        complete: () => resolve(),
      }
    );
  });
}

/**
 * Animate transitioning between two graph states by diffing elements.
 * Returns the IDs of nodes that were added, removed, and kept.
 */
export interface GraphDiff {
  addedNodeIds: string[];
  removedNodeIds: string[];
  keptNodeIds: string[];
  addedEdgeIds: string[];
  removedEdgeIds: string[];
  keptEdgeIds: string[];
}

export function computeGraphDiff(
  currentElements: { nodes: Array<{ data: { id: string } }>; edges: Array<{ data: { id: string } }> },
  newElements: Array<{ data: { id: string; source?: string; target?: string } }>
): GraphDiff {
  const currentNodeIds = new Set(currentElements.nodes.map((n) => n.data.id));
  const currentEdgeIds = new Set(currentElements.edges.map((e) => e.data.id));

  const newNodeIds = new Set<string>();
  const newEdgeIds = new Set<string>();

  newElements.forEach((el) => {
    if (el.data.source && el.data.target) {
      newEdgeIds.add(el.data.id);
    } else {
      newNodeIds.add(el.data.id);
    }
  });

  return {
    addedNodeIds: Array.from(newNodeIds).filter((id) => !currentNodeIds.has(id)),
    removedNodeIds: Array.from(currentNodeIds).filter((id) => !newNodeIds.has(id)),
    keptNodeIds: Array.from(currentNodeIds).filter((id) => newNodeIds.has(id)),
    addedEdgeIds: Array.from(newEdgeIds).filter((id) => !currentEdgeIds.has(id)),
    removedEdgeIds: Array.from(currentEdgeIds).filter((id) => !newEdgeIds.has(id)),
    keptEdgeIds: Array.from(currentEdgeIds).filter((id) => newEdgeIds.has(id)),
  };
}

/**
 * Apply a graph update with animated transitions.
 * Fades out removed elements, adds new elements with fade-in, and runs layout.
 */
export async function animateGraphUpdate(
  cy: Core,
  newElements: any[],
  layoutOptions: any,
  duration: number = DEFAULT_DURATION
): Promise<void> {
  const effectiveDuration = getEffectiveDuration(duration, cy.nodes().length);

  // Compute what changed
  const currentNodes = cy.nodes().map((n) => ({ data: { id: n.id() } }));
  const currentEdges = cy.edges().map((e) => ({ data: { id: e.id() } }));
  const diff = computeGraphDiff({ nodes: currentNodes, edges: currentEdges }, newElements);

  // If no animation or complete replacement, do it instantly
  if (effectiveDuration === 0 || (diff.keptNodeIds.length === 0 && diff.removedNodeIds.length === 0)) {
    cy.batch(() => {
      cy.elements().remove();
      cy.add(newElements);
    });
    if (newElements.length > 0) {
      cy.layout(layoutOptions).run();
    }
    return;
  }

  // Phase 1: Fade out removed elements
  const removePromises: Promise<void>[] = [];
  diff.removedNodeIds.forEach((id) => {
    removePromises.push(animateNodeDisappear(cy, id, effectiveDuration * 0.5));
  });
  diff.removedEdgeIds.forEach((id) => {
    const edge = cy.getElementById(id);
    if (edge.length > 0) {
      edge.style({ opacity: 0 });
    }
  });

  if (removePromises.length > 0) {
    await Promise.all(removePromises);
  }

  // Phase 2: Remove faded elements and add new ones
  cy.batch(() => {
    // Remove elements that were faded out
    diff.removedNodeIds.forEach((id) => {
      const el = cy.getElementById(id);
      if (el.length > 0) el.remove();
    });
    diff.removedEdgeIds.forEach((id) => {
      const el = cy.getElementById(id);
      if (el.length > 0) el.remove();
    });

    // Add new elements with initial opacity 0
    const addedElements = newElements.filter((el) => {
      const id = el.data.id;
      return diff.addedNodeIds.includes(id) || diff.addedEdgeIds.includes(id);
    });

    addedElements.forEach((el) => {
      cy.add({ ...el, style: { opacity: 0 } });
    });
  });

  // Phase 3: Run layout on new arrangement
  if (cy.elements().length > 0) {
    await new Promise<void>((resolve) => {
      const layout = cy.layout({
        ...layoutOptions,
        animate: effectiveDuration > 0,
        animationDuration: effectiveDuration,
        stop: () => resolve(),
      });
      layout.run();
    });
  }

  // Phase 4: Fade in new elements
  const appearPromises: Promise<void>[] = [];
  diff.addedNodeIds.forEach((id) => {
    appearPromises.push(animateNodeAppear(cy, id, effectiveDuration * 0.5));
  });
  diff.addedEdgeIds.forEach((id) => {
    appearPromises.push(animateEdgeGrow(cy, id, effectiveDuration * 0.5));
  });

  if (appearPromises.length > 0) {
    await Promise.all(appearPromises);
  }
}

/**
 * Animate filtering: show some nodes and hide others with smooth transitions
 */
export async function animateFilterTransition(
  cy: Core,
  showNodeIds: string[],
  hideNodeIds: string[],
  duration: number = DEFAULT_DURATION
): Promise<void> {
  const effectiveDuration = getEffectiveDuration(duration, cy.nodes().length);

  if (effectiveDuration === 0) {
    cy.batch(() => {
      showNodeIds.forEach((id) => {
        const node = cy.getElementById(id);
        if (node.length > 0) node.style({ opacity: 1, display: 'element' });
      });
      hideNodeIds.forEach((id) => {
        const node = cy.getElementById(id);
        if (node.length > 0) node.style({ display: 'none' });
      });
    });
    return;
  }

  // Fade out nodes being hidden
  const fadeOutPromises = hideNodeIds.map((id) => animateNodeDisappear(cy, id, effectiveDuration));
  await Promise.all(fadeOutPromises);

  // Hide them from layout
  cy.batch(() => {
    hideNodeIds.forEach((id) => {
      const node = cy.getElementById(id);
      if (node.length > 0) node.style({ display: 'none' });
    });

    // Ensure nodes being shown are visible
    showNodeIds.forEach((id) => {
      const node = cy.getElementById(id);
      if (node.length > 0) node.style({ display: 'element', opacity: 0 });
    });
  });

  // Fade in nodes being shown
  const fadeInPromises = showNodeIds.map((id) => animateNodeAppear(cy, id, effectiveDuration));
  await Promise.all(fadeInPromises);
}

/**
 * Animate scope change (personal <-> organization)
 * Zooms out, fades everything, replaces data, fades back in
 */
export async function animateScopeChange(
  cy: Core,
  newElements: any[],
  layoutOptions: any,
  duration: number = DEFAULT_DURATION
): Promise<void> {
  const effectiveDuration = getEffectiveDuration(duration, cy.nodes().length);

  if (effectiveDuration === 0) {
    cy.batch(() => {
      cy.elements().remove();
      cy.add(newElements);
    });
    if (newElements.length > 0) {
      cy.layout(layoutOptions).run();
    }
    return;
  }

  // Phase 1: Fade out all current elements
  await new Promise<void>((resolve) => {
    if (cy.elements().length === 0) {
      resolve();
      return;
    }
    cy.elements().animate(
      { style: { opacity: 0 } },
      { duration: effectiveDuration * 0.4, complete: () => resolve() }
    );
  });

  // Phase 2: Replace elements
  cy.batch(() => {
    cy.elements().remove();
    newElements.forEach((el) => {
      cy.add({ ...el, style: { opacity: 0 } });
    });
  });

  // Phase 3: Run layout
  if (cy.elements().length > 0) {
    await new Promise<void>((resolve) => {
      cy.layout({
        ...layoutOptions,
        animate: false, // Don't animate layout itself, we'll fade in
        stop: () => resolve(),
      }).run();
    });
  }

  // Phase 4: Fade in new elements
  await new Promise<void>((resolve) => {
    if (cy.elements().length === 0) {
      resolve();
      return;
    }
    cy.elements().animate(
      { style: { opacity: 1 } },
      { duration: effectiveDuration * 0.4, complete: () => resolve() }
    );
  });
}

/**
 * Animate cluster expansion (explode cluster into individual nodes)
 */
export async function animateClusterExpand(
  cy: Core,
  clusterId: string,
  duration: number = DEFAULT_DURATION
): Promise<void> {
  const effectiveDuration = getEffectiveDuration(duration, cy.nodes().length);
  const clusterNode = cy.getElementById(clusterId);
  if (!clusterNode || clusterNode.length === 0) return;

  if (effectiveDuration === 0) return;

  // Get position of cluster node for radial animation origin
  const clusterPos = clusterNode.position();

  // Find child nodes (nodes with parent = clusterId)
  const children = cy.nodes(`[parent = "${clusterId}"]`);
  if (children.length === 0) return;

  // Start children at cluster center position
  children.forEach((child) => {
    child.position(clusterPos);
    child.style({ opacity: 0 });
  });

  // Animate children fading in
  await new Promise<void>((resolve) => {
    children.animate(
      { style: { opacity: 1 } },
      { duration: effectiveDuration, easing: DEFAULT_EASING, complete: () => resolve() }
    );
  });
}

/**
 * Animate cluster collapse (implode individual nodes into cluster)
 */
export async function animateClusterCollapse(
  cy: Core,
  clusterId: string,
  duration: number = DEFAULT_DURATION
): Promise<void> {
  const effectiveDuration = getEffectiveDuration(duration, cy.nodes().length);
  const clusterNode = cy.getElementById(clusterId);
  if (!clusterNode || clusterNode.length === 0) return;

  if (effectiveDuration === 0) return;

  // Find child nodes
  const children = cy.nodes(`[parent = "${clusterId}"]`);
  if (children.length === 0) return;

  // Fade out children
  await new Promise<void>((resolve) => {
    children.animate(
      { style: { opacity: 0 } },
      { duration: effectiveDuration, easing: DEFAULT_EASING, complete: () => resolve() }
    );
  });
}

/**
 * Apply temporal age-based styling to nodes.
 * Nodes closer to endDate get full opacity; nodes closer to startDate are dimmer.
 */
export function applyTemporalStyling(
  cy: Core,
  startDate: Date,
  endDate: Date
): void {
  const range = endDate.getTime() - startDate.getTime();
  if (range <= 0) return;

  cy.nodes().forEach((node) => {
    const createdAt = node.data('createdAt') || node.data('created_at') || node.data('sentAt');
    if (!createdAt) return;

    const nodeTime = new Date(createdAt).getTime();
    // Normalize to 0-1 range (0 = oldest, 1 = newest)
    const age = Math.max(0, Math.min(1, (nodeTime - startDate.getTime()) / range));

    // Map age to opacity: 0.3 (oldest) to 1.0 (newest)
    const opacity = 0.3 + age * 0.7;

    node.style({ 'background-opacity': opacity });

    // Apply temporal class for additional styling
    node.removeClass('temporal-old temporal-new');
    if (age < 0.3) {
      node.addClass('temporal-old');
    } else if (age > 0.7) {
      node.addClass('temporal-new');
    }
  });
}

/**
 * Clear temporal styling from all nodes
 */
export function clearTemporalStyling(cy: Core): void {
  cy.nodes().forEach((node) => {
    node.style({ 'background-opacity': 0.9 }); // Reset to default
    node.removeClass('temporal-old temporal-new');
  });
}
